import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, Send, UserCircle, Building2, Calculator, Users, Plus, Trash2, CalendarClock, KeyRound, Copy, Camera, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const input = "mt-2 w-full rounded-lg bg-[#171E2E] border border-[#2A3550] px-4 py-2.5 text-sm focus:border-[#38BDF8] focus:outline-none transition-colors duration-200";
const cell = "rounded-lg bg-[#171E2E] border border-[#2A3550] px-3 py-2 text-sm focus:border-[#38BDF8] focus:outline-none transition-colors duration-200";

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isCorporate = user?.role === "corporate";
  const isOwner = user?.role === "owner";
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [mgrs, setMgrs] = useState([]);
  const [savingMgrs, setSavingMgrs] = useState(false);
  const [sendingMonthly, setSendingMonthly] = useState(false);

  const { data } = useQuery({ queryKey: ["profile"], queryFn: () => api.get("/profile").then((r) => r.data) });
  const { data: managers } = useQuery({ queryKey: ["managers"], queryFn: () => api.get("/managers").then((r) => r.data) });
  const { data: stores } = useQuery({ queryKey: ["stores"], queryFn: () => api.get("/stores").then((r) => r.data) });
  useEffect(() => { if (data) setP(data); }, [data]);
  useEffect(() => { if (managers && !isCorporate) setMgrs(managers.map((m) => ({ ...m, _uid: m._uid || (crypto.randomUUID?.() || `${m.email}-${Math.random()}`) }))); }, [managers, isCorporate]);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const storeName = (id) => { const s = (stores || []).find((x) => x.id === id); return s ? `#${s.number} · ${s.name}` : "—"; };

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("Image must be under 3 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => set("photo_url", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/profile", p); toast.success("Profile saved"); }
    catch { toast.error("Could not save profile"); }
    setSaving(false);
  };

  const sendAccountant = async () => {
    if (!p?.accountant_email) { toast.error("Add your accountant's email first"); return; }
    setSending(true);
    try {
      await api.put("/profile", p);
      const { data: res } = await api.post("/profile/send-to-accountant", p);
      if (res.sent) toast.success(`Business details emailed to ${p.accountant_email}`);
      else toast.error("Email could not be sent");
    } catch { toast.error("Email could not be sent"); }
    setSending(false);
  };

  const sendMonthly = async () => {
    if (!p?.accountant_email) { toast.error("Add your accountant's email first"); return; }
    setSendingMonthly(true);
    try {
      await api.put("/profile", p);
      const { data: res } = await api.post("/profile/send-monthly-summary");
      if (res.sent) toast.success(`Monthly summary emailed for ${res.period}`);
      else toast.error("Summary could not be sent (check the accountant email)");
    } catch { toast.error("Summary could not be sent"); }
    setSendingMonthly(false);
  };

  const addMgr = () => setMgrs((m) => [...m, { name: "", email: "", phone: "", store_id: "", notes: "", _uid: crypto.randomUUID?.() || `new-${Date.now()}-${Math.random()}` }]);
  const setMgr = (i, k, v) => setMgrs((m) => m.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const removeMgr = (i) => setMgrs((m) => m.filter((_, idx) => idx !== i));
  const saveMgrs = async () => {
    setSavingMgrs(true);
    try { await api.put("/managers", { managers: mgrs }); toast.success("Managers saved"); }
    catch { toast.error("Could not save managers"); }
    setSavingMgrs(false);
  };

  // Manager LOGIN accounts (store-scoped)
  const { data: mgrLogins } = useQuery({ queryKey: ["manager-logins"], queryFn: () => api.get("/managers/logins").then((r) => r.data) });
  const [cred, setCred] = useState(null);
  const hasLogin = (email) => (mgrLogins || []).some((l) => l.email === (email || "").trim().toLowerCase());
  const enableLogin = async (m) => {
    if (!m.email || !m.store_id) { toast.error("Add an email and select a store first"); return; }
    try {
      await api.put("/managers", { managers: mgrs });
      const { data } = await api.post("/managers/create-login", { name: m.name, email: m.email, phone: m.phone, store_id: m.store_id, notes: m.notes || "" });
      setCred(data);
      toast.success(`Login created for ${data.email}`);
      qc.invalidateQueries({ queryKey: ["manager-logins"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create login"); }
  };
  const resetLogin = async (email) => {
    try { const { data } = await api.post("/managers/reset-login-password", { email }); setCred(data); toast.success("Password reset"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not reset password"); }
  };
  const removeLogin = async (email) => {
    try { await api.delete(`/managers/login/${encodeURIComponent(email)}`); toast.success("Login removed"); qc.invalidateQueries({ queryKey: ["manager-logins"] }); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove login"); }
  };
  const copyCred = () => { navigator.clipboard?.writeText(`${cred.email} / ${cred.password}`); toast.success("Copied"); };

  if (!p) return <div className="text-slate-400">Loading…</div>;

  return (
    <div data-testid="profile-page" className="w-full min-w-0 overflow-x-hidden">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">
        {isCorporate ? "Corporate · HQ Profile" : isOwner ? "Owner Profile" : "Manager Profile"}
      </div>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tight">Your business information</h1>
      <p className="mt-2 text-sm text-slate-400 max-w-2xl">
        Keep the details for this account in one place — your business identity, who owns it, your registration number, and your accountant.
        {isCorporate
          ? " As HQ, this represents the parent company (for example, whoever owns the network)."
          : " This is your own store business."}
        {" "}You can email a clean summary straight to your accountant.
      </p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6 min-w-0">
        {/* Contact */}
        <div className="surface-card p-6 space-y-5 min-w-0">
          <div className="flex items-center gap-2"><UserCircle className="h-5 w-5 text-[#38BDF8]" /><h2 className="font-display text-lg font-medium">Your contact</h2></div>
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              {p.photo_url ? (
                <img src={p.photo_url} alt="Profile" data-testid="profile-photo" className="h-20 w-20 rounded-full object-cover border border-[#2A3550]" />
              ) : (
                <div data-testid="profile-photo-placeholder" className="h-20 w-20 rounded-full border border-[#2A3550] bg-[#171E2E] flex items-center justify-center text-slate-500">
                  <UserCircle className="h-10 w-10" />
                </div>
              )}
              {p.photo_url && (
                <button type="button" onClick={() => set("photo_url", "")} data-testid="profile-photo-remove"
                  className="absolute -top-1 -right-1 rounded-full bg-[#1E2638] border border-[#2A3550] p-1 text-slate-300 hover:text-red-400 transition-colors duration-200">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="min-w-0">
              <label data-testid="profile-photo-upload" className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium border border-[#2A3550] bg-[#1E2638] hover:border-[#38BDF8] text-slate-100 transition-colors duration-200 cursor-pointer">
                <Camera className="h-4 w-4" /> {p.photo_url ? "Change photo" : "Add a photo"}
                <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="profile-photo-input" />
              </label>
              <p className="mt-1.5 text-xs text-slate-400">JPG or PNG, up to 3&nbsp;MB. Saved when you press Save profile.</p>
            </div>
          </div>
          <Field label="Full name"><input data-testid="profile-contact-name" className={input} value={p.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} /></Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Email"><input data-testid="profile-contact-email" className={input} value={p.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} /></Field>
            <Field label="Phone"><input data-testid="profile-contact-phone" className={input} value={p.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} placeholder="+1 555 000 0000" /></Field>
          </div>
        </div>

        {/* Business */}
        <div className="surface-card p-6 space-y-5 min-w-0">
          <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-[#38BDF8]" /><h2 className="font-display text-lg font-medium">Business</h2></div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Business name" hint="e.g. Sport Clips"><input data-testid="profile-business-name" className={input} value={p.business_name || ""} onChange={(e) => set("business_name", e.target.value)} /></Field>
            <Field label="Owned by / parent company" hint="e.g. Welcome to the Colosseum Inc."><input data-testid="profile-parent-company" className={input} value={p.parent_company || ""} onChange={(e) => set("parent_company", e.target.value)} /></Field>
          </div>
          <Field label="Business / registration number" hint="ABN, EIN or company number"><input data-testid="profile-business-number" className={input} value={p.business_number || ""} onChange={(e) => set("business_number", e.target.value)} /></Field>
          <Field label="Address"><input data-testid="profile-address" className={input} value={p.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="Street address" /></Field>
          <div className="grid sm:grid-cols-4 gap-3">
            <Field label="City"><input data-testid="profile-city" className={input} value={p.city || ""} onChange={(e) => set("city", e.target.value)} /></Field>
            <Field label="State"><input data-testid="profile-state" className={input} value={p.state || ""} onChange={(e) => set("state", e.target.value)} /></Field>
            <Field label="Postcode"><input data-testid="profile-postal" className={input} value={p.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)} /></Field>
            <Field label="Country"><input data-testid="profile-country" className={input} value={p.country || ""} onChange={(e) => set("country", e.target.value)} /></Field>
          </div>
        </div>

        {/* Accounting */}
        <div className="surface-card p-6 space-y-5 lg:col-span-2 min-w-0">
          <div className="flex items-center gap-2"><Calculator className="h-5 w-5 text-[#38BDF8]" /><h2 className="font-display text-lg font-medium">Accounting</h2></div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Accountant name"><input data-testid="profile-accountant-name" className={input} value={p.accountant_name || ""} onChange={(e) => set("accountant_name", e.target.value)} /></Field>
            <Field label="Accountant email" hint="Where your business summary is sent"><input data-testid="profile-accountant-email" className={input} value={p.accountant_email || ""} onChange={(e) => set("accountant_email", e.target.value)} placeholder="accountant@firm.com" /></Field>
          </div>
          <Field label="Notes"><textarea rows={3} data-testid="profile-notes" className={input} value={p.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Anything your accountant should know…" /></Field>
          <div className="flex items-center justify-between gap-4 rounded-lg border border-[#2A3550] bg-[#171E2E] px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-medium">Monthly summary to accountant</div>
              <div className="text-xs text-slate-400">Auto-email reconciled revenue &amp; leakage totals at month-end so their books stay in sync.</div>
            </div>
            <Switch checked={!!p.accountant_monthly} onCheckedChange={(v) => set("accountant_monthly", v)} data-testid="accountant-monthly-toggle" />
          </div>
          <button onClick={sendMonthly} disabled={sendingMonthly} data-testid="send-monthly-btn"
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium border border-[#2A3550] bg-[#1E2638] hover:border-[#38BDF8] text-slate-100 transition-colors duration-200 disabled:opacity-60">
            <CalendarClock className="h-4 w-4" /> {sendingMonthly ? "Sending…" : "Send this month's summary now"}
          </button>
        </div>

        {/* Managers */}
        <div className="surface-card p-6 space-y-5 lg:col-span-2 min-w-0" data-testid="managers-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2"><Users className="h-5 w-5 text-[#38BDF8]" />
              <h2 className="font-display text-lg font-medium">{isCorporate ? "Managers across the network" : "Your store managers"}</h2>
            </div>
            {isOwner && (
              <button onClick={addMgr} data-testid="manager-add-btn"
                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium border border-[#2A3550] bg-[#1E2638] hover:border-[#38BDF8] text-slate-100 transition-colors duration-200">
                <Plus className="h-4 w-4" /> Add manager
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400">
            {isCorporate
              ? "Read-only view of every manager each store owner has added, with the owner they report to and whether they have a login."
              : "Add trusted managers here. Enable a login to give them a store-scoped sign-in — they'll only see their assigned store."}
          </p>

          {cred && (
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4" data-testid="manager-cred-panel">
              <div className="text-sm font-medium text-emerald-300">Login ready — share these credentials with {cred.email}</div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                <code className="rounded bg-[#0E131F] px-2 py-1 border border-[#2A3550]" data-testid="manager-cred-email">{cred.email}</code>
                <code className="rounded bg-[#0E131F] px-2 py-1 border border-[#2A3550]" data-testid="manager-cred-password">{cred.password}</code>
                <button onClick={copyCred} data-testid="manager-cred-copy" className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200"><Copy className="h-3.5 w-3.5" /> Copy</button>
                <button onClick={() => setCred(null)} className="text-xs text-slate-400 hover:text-slate-200">Dismiss</button>
              </div>
              <p className="mt-2 text-xs text-slate-400">
                {cred.emailed ? "We've emailed these sign-in details to the manager." : "Email couldn't be sent — please share these details manually."} This password is shown once; the manager only sees their assigned store.
              </p>
            </div>
          )}

          {isCorporate ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="managers-hq-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-400 border-b border-[#2A3550]">
                    <th className="py-3 pr-4">Manager</th><th className="py-3 pr-4">Email</th><th className="py-3 pr-4">Phone</th>
                    <th className="py-3 pr-4">Store</th><th className="py-3 pr-4">Reports to (owner)</th><th className="py-3 pr-4">Login</th>
                  </tr>
                </thead>
                <tbody>
                  {(managers || []).map((m, i) => (
                    <tr key={m.email || `hq-${i}`} className="border-b border-[#232C40]" data-testid={`manager-hq-row-${i}`}>
                      <td className="py-3 pr-4 font-medium">{m.name || "—"}</td>
                      <td className="py-3 pr-4 text-slate-400">{m.email || "—"}</td>
                      <td className="py-3 pr-4 text-slate-400">{m.phone || "—"}</td>
                      <td className="py-3 pr-4 text-slate-400">{m.store_id ? storeName(m.store_id) : "—"}</td>
                      <td className="py-3 pr-4 text-slate-400">{m.owner_name || m.owner_email || "—"}</td>
                      <td className="py-3 pr-4">{hasLogin(m.email) ? <span className="text-emerald-400">Active</span> : <span className="text-slate-500">—</span>}</td>
                    </tr>
                  ))}
                  {(!managers || managers.length === 0) && (
                    <tr><td colSpan={6} className="py-6 text-center text-slate-400">No managers added by any owner yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : isOwner ? (
            <div className="space-y-3">
              {mgrs.map((m, i) => {
                const active = hasLogin(m.email);
                return (
                <div key={m._uid || i} className="rounded-lg border border-[#232C40] p-3 space-y-2.5" data-testid={`manager-row-${i}`}>
                  <div className="grid md:grid-cols-12 gap-2 items-center">
                    <input className={`${cell} md:col-span-3`} placeholder="Name" value={m.name || ""} data-testid={`manager-name-${i}`} onChange={(e) => setMgr(i, "name", e.target.value)} />
                    <input className={`${cell} md:col-span-3`} placeholder="Email" value={m.email || ""} data-testid={`manager-email-${i}`} onChange={(e) => setMgr(i, "email", e.target.value)} />
                    <input className={`${cell} md:col-span-2`} placeholder="Phone" value={m.phone || ""} data-testid={`manager-phone-${i}`} onChange={(e) => setMgr(i, "phone", e.target.value)} />
                    <select className={`${cell} md:col-span-3`} value={m.store_id || ""} data-testid={`manager-store-${i}`} onChange={(e) => setMgr(i, "store_id", e.target.value)}>
                      <option value="">Store (required for login)</option>
                      {(stores || []).map((s) => <option key={s.id} value={s.id}>#{s.number} · {s.name}</option>)}
                    </select>
                    <button onClick={() => removeMgr(i)} data-testid={`manager-remove-${i}`}
                      className="md:col-span-1 inline-flex items-center justify-center rounded-lg p-2 border border-[#2A3550] text-slate-400 hover:text-red-400 hover:border-red-500/40 transition-colors duration-200">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {active ? (
                      <>
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-400" data-testid={`manager-login-active-${i}`}><KeyRound className="h-3.5 w-3.5" /> Login active</span>
                        <button onClick={() => resetLogin(m.email)} data-testid={`manager-reset-${i}`} className="text-xs text-slate-300 hover:text-[#38BDF8] transition-colors duration-200">Reset password</button>
                        <button onClick={() => removeLogin(m.email)} data-testid={`manager-remove-login-${i}`} className="text-xs text-slate-400 hover:text-red-400 transition-colors duration-200">Remove login</button>
                      </>
                    ) : (
                      <button onClick={() => enableLogin(m)} data-testid={`manager-enable-login-${i}`}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium border border-[#2A3550] bg-[#1E2638] hover:border-[#38BDF8] text-slate-100 transition-colors duration-200">
                        <KeyRound className="h-3.5 w-3.5" /> Enable login
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
              {mgrs.length === 0 && <div className="text-sm text-slate-400">No managers yet — click “Add manager”.</div>}
              <button onClick={saveMgrs} disabled={savingMgrs} data-testid="managers-save-btn"
                className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-opacity duration-200 hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: "var(--brand)" }}>
                <Save className="h-4 w-4" /> {savingMgrs ? "Saving…" : "Save managers"}
              </button>
            </div>
          ) : (
            <div className="text-sm text-slate-400" data-testid="manager-readonly-note">Manager accounts are set up by your store owner.</div>
          )}
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <button onClick={save} disabled={saving} data-testid="profile-save-btn"
          className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-opacity duration-200 hover:opacity-90 disabled:opacity-60"
          style={{ backgroundColor: "var(--brand)" }}>
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save profile"}
        </button>
        <button onClick={sendAccountant} disabled={sending} data-testid="profile-send-accountant-btn"
          className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium border border-[#2A3550] bg-[#1E2638] hover:border-[#38BDF8] text-slate-100 transition-colors duration-200 disabled:opacity-60">
          <Send className="h-4 w-4" /> {sending ? "Sending…" : "Email details to accountant"}
        </button>
      </div>
    </div>
  );
}
